import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import QRCode from 'qrcode.react';
import { encryptData } from '../utils/crypto';
import { saveToStorage, getFromStorage } from '../utils/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticator } from 'otplib';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Typography, Card, Button, Grid, TextField, IconButton, CircularProgress,
  Alert, ThemeProvider, Checkbox, FormControlLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Stepper, Step, StepLabel, InputAdornment, Tooltip
} from '@mui/material';
import {
  AddCircle, Key, FileUpload, ContentCopy, ArrowBack, ArrowForward, CheckCircle, ErrorOutline, Shield,
  Google, Phone, Lock, RestartAlt, Wallet, Visibility, VisibilityOff, Warning, VerifiedUser,
  KeyOutlined, Password, QrCode, Check, Info, FiberManualRecord, Public, LockOpen, VpnKey, UploadFile
} from '@mui/icons-material';
import { createTheme, styled } from '@mui/material/styles';

// Enhanced monochromatic cyberpunk theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#000000', paper: '#0A0A0A' },
    primary: { main: '#FFFFFF', contrastText: '#000000' },
    secondary: { main: '#FFFFFF', contrastText: '#000000' }, // Neon green for accents
    text: { primary: '#FFFFFF', secondary: '#FFFFFF' },
    divider: '#1C2526',
    action: {
      active: '#FFFFFF',
      hover: '#1C2526',
      selected: '#2A3439',
      disabled: '#424242',
      disabledBackground: '#1C2526'
    },
    error: { main: '#FF4D4D' },
    warning: { main: '#FFAA00' },
    info: { main: '#FFFFFF' },
    success: { main: '#FFFFFF' }
  },
  typography: {
    fontFamily: '"IBM Plex Mono", monospace',
    h4: { fontSize: '2.2rem', fontWeight: 800, letterSpacing: '3px', textTransform: 'uppercase' },
    h5: { fontSize: '1.6rem', fontWeight: 700, letterSpacing: '2px' },
    h6: { fontSize: '1.3rem', fontWeight: 600, letterSpacing: '1px' },
    body1: { fontSize: '1rem', lineHeight: 1.8, letterSpacing: '1px' },
    body2: { fontSize: '0.85rem', lineHeight: 1.6, letterSpacing: '0.5px' }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          background: '#0A0A0A',
          border: '1px solid #1C2526',
          boxShadow: '0 0 20px rgba(0,255,0,0.1)',
          transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
          '&:hover': { 
            borderColor: '#FFFFFF',
            boxShadow: '0 0 25px rgba(0,255,0,0.2)'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          textTransform: 'uppercase',
          padding: '12px 24px',
          fontWeight: 700,
          letterSpacing: '2px',
          background: 'transparent',
          color: '#FFFFFF',
          border: '2px solid #FFFFFF',
          transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
          '&:hover': { 
            background: '#FFFFFF', 
            color: '#000000',
            border: '2px solid #FFFFFF',
            boxShadow: '0 0 15px rgba(0,255,0,0.3)'
          },
          '&.Mui-disabled': {
            borderColor: '#424242',
            color: '#424242',
            opacity: 0.5
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '0',
            backgroundColor: '#0A0A0A',
            '& fieldset': { border: '1px solid #1C2526' },
            '&:hover fieldset': { borderColor: '#FFFFFF' },
            '&.Mui-focused fieldset': { borderColor: '#FFFFFF', borderWidth: '2px' },
            '&.Mui-error fieldset': { borderColor: '#FF4D4D' }
          },
          '& .MuiInputLabel-root': { 
            color: '#FFFFFF',
            fontWeight: 600,
            letterSpacing: '1px'
          },
          '& .MuiInputBase-input': { 
            fontFamily: '"IBM Plex Mono", monospace',
            color: '#FFFFFF',
            fontSize: '0.95rem'
          }
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          background: 'transparent',
          padding: '24px 0'
        }
      }
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: '#1C2526',
          borderWidth: '2px'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          background: '#0A0A0A',
          border: '1px solid #1C2526',
          color: '#FFFFFF',
          alignItems: 'center',
          '& .MuiAlert-icon': { color: '#FFFFFF' },
          '&.MuiAlert-standardError': { borderColor: '#FF4D4D', '& .MuiAlert-icon': { color: '#FF4D4D' } },
          '&.MuiAlert-standardWarning': { borderColor: '#FFAA00', '& .MuiAlert-icon': { color: '#FFAA00' } },
          '&.MuiAlert-standardInfo': { borderColor: '#FFFFFF', '& .MuiAlert-icon': { color: '#FFFFFF' } },
          '&.MuiAlert-standardSuccess': { borderColor: '#FFFFFF', '& .MuiAlert-icon': { color: '#FFFFFF' } }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '0',
          background: '#0A0A0A',
          border: '1px solid #1C2526',
          color: '#FFFFFF',
          fontWeight: 600,
          letterSpacing: '1px',
          '&:hover': {
            borderColor: '#FFFFFF',
            background: '#1C2526'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#0A0A0A',
          border: '1px solid #1C2526',
          borderRadius: '0',
          boxShadow: '0 0 20px rgba(0,255,0,0.2)'
        }
      }
    }
  }
});

// ASCII art logo
const asciiLogo = `
  AAA   VV     VV  OOOOO      SSSSS  TTTTTTT   AAA   TTTTTTT IIIII  OOOOO  NN   NN 
 AAAAA  VV     VV OO   OO    SS        TTT    AAAAA    TTT    III  OO   OO NNN  NN 
AA   AA  VV   VV  OO   OO     SSSSS    TTT   AA   AA   TTT    III  OO   OO NN N NN 
AAAAAAA   VV VV   OO   OO         SS   TTT   AAAAAAA   TTT    III  OO   OO NN  NNN 
AA   AA    VVV     OOOO0      SSSSS    TTT   AA   AA   TTT   IIIII  OOOO0  NN   NN 
                                                                                   
                                                                  `;

function CreateWallet({ setWallet, setIsSessionActive }) {
  const [step, setStep] = useState('welcome');
  const [mode, setMode] = useState(null);
  const [mnemonic, setMnemonic] = useState('');
  const [verifyWords, setVerifyWords] = useState([]);
  const [userVerifyInputs, setUserVerifyInputs] = useState(['', '', '']);
  const [importJson, setImportJson] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [recoveryWords, setRecoveryWords] = useState(Array(12).fill(''));
  const [walletAddress, setWalletAddress] = useState('');
  const [reset2FA, setReset2FA] = useState(false);
  const [showMnemonic, setShowMnemonic] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const recoveryWordsRef = useRef(Array(12).fill(''));
  const navigate = useNavigate();

  // Dynamic steps based on mode
  const getSteps = () => {
    if (mode === 'new') {
      return ['welcome', 'mode-select', 'seed-gen', 'verify', 'security-info', 'password', '2fa'];
    } else if (mode === 'recover' || mode === 'import') {
      return ['welcome', 'mode-select', 'seed-gen', 'security-info', 'password', '2fa'];
    }
    return ['welcome', 'mode-select', 'seed-gen', 'security-info', 'password', '2fa'];
  };

  const steps = getSteps();

  // Step configuration with labels and icons
  const getStepConfig = () => {
    const baseConfig = [
      { name: 'welcome', label: 'WELCOME', icon: <Shield sx={{ fontSize: 24 }} /> },
      { name: 'mode-select', label: 'MODE', icon: <Wallet sx={{ fontSize: 24 }} /> },
      { name: 'seed-gen', label: 'SEED', icon: <Key sx={{ fontSize: 24 }} /> },
    ];

    if (mode === 'new') {
      baseConfig.push({ name: 'verify', label: 'VERIFY', icon: <Check sx={{ fontSize: 24 }} /> });
    }

    baseConfig.push(
      { name: 'security-info', label: 'SECURITY', icon: <Shield sx={{ fontSize: 24 }} /> },
      { name: 'password', label: 'PASSWORD', icon: <Lock sx={{ fontSize: 24 }} /> },
      { name: '2fa', label: '2FA', icon: <QrCode sx={{ fontSize: 24 }} /> }
    );

    return baseConfig;
  };

  const stepConfig = getStepConfig();

  // Custom step icon component
  const DotStepIcon = (props) => {
    const { active, completed, className, icon } = props;
    const stepIndex = props.stepIndex;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title={stepConfig[stepIndex]?.label || ''}>
          <Box sx={{
            fontSize: active ? '24px' : '20px',
            color: active ? '#FFFFFF' : completed ? '#FFFFFF' : '#424242',
            transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
            transform: active ? 'scale(1.2)' : 'scale(1)',
            '&:hover': { color: '#FFFFFF' }
          }}>
            {stepConfig[stepIndex]?.icon}
          </Box>
        </Tooltip>
      </Box>
    );
  };

  // Check for existing wallet and redirect
  useEffect(() => {
    const checkWallet = async () => {
      const encryptedWallet = await getFromStorage('cryptoWallet');
      if (encryptedWallet) navigate('/login');
    };
    checkWallet();
  }, [navigate]);

  // Handle copy feedback
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);

  // Load 2FA secret
  useEffect(() => {
    if (step === '2fa' && walletAddress) {
      const loadTwoFASecret = async () => {
        const storedSecret = await getFromStorage(`2faSecret_${walletAddress}`);
        if (storedSecret && !reset2FA) setTwoFASecret(storedSecret);
        else setTwoFASecret(authenticator.generateSecret());
      };
      loadTwoFASecret();
    }
  }, [step, walletAddress, reset2FA]);

  // Generate new wallet
  const generateNewWallet = () => {
    try {
      const wallet = ethers.Wallet.createRandom();
      const phrase = wallet.mnemonic.phrase.split(' ').slice(0, 12).join(' ');
      setMnemonic(phrase);
      setWalletAddress(wallet.address);
      const words = phrase.split(' ');
      const indices = [];
      while (indices.length < 3) {
        const idx = Math.floor(Math.random() * 12);
        if (!indices.includes(idx)) indices.push(idx);
      }
      setVerifyWords(indices.map(idx => ({ index: idx + 1, word: words[idx] })));
      const newWords = Array(12).fill('');
      setRecoveryWords(newWords);
      recoveryWordsRef.current = newWords;
      saveToStorage('recoveryWords', newWords);
      return wallet;
    } catch (error) {
      throw new Error('WALLET GENERATION FAILED');
    }
  };

  // Password validation
  const validatePassword = (pass) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/.test(pass);
  };

  // Mnemonic validation
  const validateMnemonic = (phrase) => {
    if (!phrase || typeof phrase !== 'string') return false;
    const wordCount = phrase.trim().split(/\s+/g).length;
    if (wordCount !== 12) return false;
    return ethers.utils.isValidMnemonic(phrase.trim());
  };

  // Verification words validation
  const validateVerificationWords = () => {
    return verifyWords.every((item, index) => userVerifyInputs[index].toLowerCase() === item.word.toLowerCase());
  };

  // 2FA code validation
  const validateTwoFACode = (code) => {
    try {
      return authenticator.verify({ token: code, secret: twoFASecret });
    } catch {
      return false;
    }
  };

  // Copy to clipboard
  const handleCopyToClipboard = (text, isSingleWord = false) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ fontSize: 20 }} />
        <Typography>{isSingleWord ? 'WORD COPIED' : 'PHRASE COPIED'}</Typography>
      </Box>,
      {
        position: 'bottom-right',
        autoClose: 2000,
        style: { 
          background: '#0A0A0A',
          color: '#FFFFFF',
          border: '1px solid #1C2526',
          borderRadius: '0'
        }
      }
    );
  };

  // Paste mnemonic
  const handlePasteMnemonic = async (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text').trim();
    const words = pastedText.split(/\s+/).slice(0, 12);
    if (words.length === 12) {
      const newWords = words.map(word => word.trim());
      setRecoveryWords(newWords);
      recoveryWordsRef.current = newWords;
      await saveToStorage('recoveryWords', newWords);
      toast.info(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info sx={{ fontSize: 20 }} />
          <Typography>PHRASE PASTED SUCCESSFULLY</Typography>
        </Box>,
        {
          position: 'bottom-right',
          style: { 
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid #1C2526',
            borderRadius: '0'
          }
        }
      );
    } else {
      toast.error(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutline sx={{ fontSize: 20 }} />
          <Typography>PHRASE MUST CONTAIN 12 WORDS</Typography>
        </Box>,
        {
          position: 'bottom-right',
          style: { 
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid #1C2526',
            borderRadius: '0'
          }
        }
      );
    }
  };

  // File upload for JSON
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutline sx={{ fontSize: 20 }} />
          <Typography>PLEASE UPLOAD A JSON FILE</Typography>
        </Box>,
        {
          position: 'bottom-right',
          style: { 
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid #1C2526',
            borderRadius: '0'
          }
        }
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        JSON.parse(event.target.result);
        setImportJson(event.target.result);
        toast.success(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 20 }} />
            <Typography>JSON FILE UPLOADED</Typography>
          </Box>,
          {
            position: 'bottom-right',
            style: { 
              background: '#0A0A0A',
              color: '#FFFFFF',
              border: '1px solid #1C2526',
              borderRadius: '0'
            }
          }
        );
      } catch (error) {
        toast.error(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutline sx={{ fontSize: 20 }} />
            <Typography>INVALID JSON FILE</Typography>
          </Box>,
          {
            position: 'bottom-right',
            style: { 
              background: '#0A0A0A',
              color: '#FFFFFF',
              border: '1px solid #1C2526',
              borderRadius: '0'
            }
          }
        );
      }
    };
    reader.readAsText(file);
  };

  // Recovery input change
  const handleRecoveryInputChange = async (index, value) => {
    const currentWords = [...recoveryWords];
    currentWords[index] = value.trim();
    setRecoveryWords(currentWords);
    recoveryWordsRef.current = currentWords;
    await saveToStorage('recoveryWords', currentWords);
  };

  // Reset 2FA
  const handleReset2FA = () => {
    setReset2FA(true);
    setTwoFASecret(authenticator.generateSecret());
    setTwoFACode('');
    toast.info(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Info sx={{ fontSize: 20 }} />
        <Typography>2FA RESET - SCAN NEW CODE</Typography>
      </Box>,
      {
        position: 'bottom-right',
        style: { 
          background: '#0A0A0A',
          color: '#FFFFFF',
          border: '1px solid #1C2526',
          borderRadius: '0'
        }
      }
    );
  };

  // Continue to next step
  const handleContinue = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    try {
      if (step === 'welcome' && !acceptedTerms) throw new Error('ACCEPT TERMS TO CONTINUE');
      if (step === 'mode-select' && !mode) throw new Error('SELECT AN INITIALIZATION MODE');
      if (step === 'seed-gen' && mode === 'recover') {
        const phrase = recoveryWords.join(' ').trim();
        if (!validateMnemonic(phrase)) throw new Error('INVALID RECOVERY PHRASE');
        const wallet = ethers.Wallet.fromMnemonic(phrase);
        setMnemonic(phrase);
        setWalletAddress(wallet.address);
      }
      if (step === 'seed-gen' && mode === 'import' && !importJson) throw new Error('UPLOAD VALID JSON FILE');
      if (step === 'verify' && !validateVerificationWords()) {
        const incorrectIndices = verifyWords
          .map((item, index) => userVerifyInputs[index].toLowerCase() !== item.word.toLowerCase() ? index : -1)
          .filter(i => i !== -1);
        setErrorMessage(`VERIFICATION FAILED - CHECK WORD(S) ${incorrectIndices.map(i => i + 1).join(', ')}`);
        throw new Error(`VERIFICATION FAILED - CHECK WORD(S) ${incorrectIndices.map(i => i + 1).join(', ')}`);
      }
      if (step === 'password') {
        if (!validatePassword(password)) throw new Error('PASSWORD MUST BE 8+ CHARACTERS WITH UPPERCASE, LOWERCASE, NUMBERS, AND SYMBOLS');
        if (password !== confirmPassword) throw new Error('PASSWORDS DO NOT MATCH');
      }
      if (step === '2fa') {
        if (!twoFACode) throw new Error('ENTER 2FA CODE');
        if (!validateTwoFACode(twoFACode)) throw new Error('INVALID 2FA CODE');
        const wallet = await finalizeWalletCreation();
        setIsSessionActive(true);
        notifySuccess(wallet);
        const emptyWords = Array(12).fill('');
        setRecoveryWords(emptyWords);
        recoveryWordsRef.current = emptyWords;
        await saveToStorage('recoveryWords', emptyWords);
        return;
      }
      setStep(steps[steps.indexOf(step) + 1]);
      if (step === 'mode-select' && mode === 'new') generateNewWallet();
    } catch (error) {
      setErrorMessage(error.message);
      toast.error(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutline sx={{ fontSize: 20 }} />
          <Typography>{error.message}</Typography>
        </Box>,
        {
          position: 'bottom-right',
          style: { 
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid #1C2526',
            borderRadius: '0'
          }
        }
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Finalize wallet creation
  const finalizeWalletCreation = async () => {
    let wallet;
    try {
      if (mode === 'new' || mode === 'recover') {
        wallet = ethers.Wallet.fromMnemonic(mnemonic);
      } else if (mode === 'import') {
        wallet = await ethers.Wallet.fromEncryptedJson(importJson, password);
      }
      const walletData = { address: wallet.address, privateKey: wallet.privateKey };
      let recoveryPhrase = mode === 'new' || mode === 'recover' ? mnemonic : wallet.mnemonic?.phrase || '';
      if (recoveryPhrase) {
        const wordsArray = recoveryPhrase.split(' ').filter(word => word.trim());
        if (wordsArray.length !== 12) throw new Error('INVALID RECOVERY PHRASE');
        const encryptedRecoveryWords = await encryptData(JSON.stringify(wordsArray), password);
        // Guardar directamente el string encriptado (formato legacy compatible)
        localStorage.setItem('secure_v2_secure_v2_recoveryWords', encryptedRecoveryWords);
      } else if (mode === 'import') {
        toast.warn(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ fontSize: 20 }} />
            <Typography>NO RECOVERY PHRASE FOR IMPORTED WALLET</Typography>
          </Box>,
          {
            position: 'bottom-right',
            style: { 
              background: '#0A0A0A',
              color: '#FFFFFF',
              border: '1px solid #1C2526',
              borderRadius: '0'
            }
          }
        );
      }
      const encryptedWallet = await encryptData(JSON.stringify(walletData), password);
      // Guardar directamente el string encriptado (formato legacy compatible)
      localStorage.setItem('secure_v2_cryptoWallet', encryptedWallet);
      await saveToStorage(`2faSecret_${wallet.address}`, twoFASecret);
      await saveToStorage('sessionActive', 'true');
      return wallet;
    } catch (error) {
      throw new Error('WALLET CREATION ERROR: ' + error.message);
    }
  };

  // Success notification
  const notifySuccess = (wallet) => {
    setWallet({ address: wallet.address });
    toast.success(
      <Box sx={{ textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 24, mb: 1 }} />
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          WALLET CREATED
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontSize: '0.8rem' }}>
          {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)}
        </Typography>
      </Box>,
      {
        position: 'bottom-right',
        autoClose: 2000,
        style: { 
          background: '#0A0A0A',
          color: '#FFFFFF',
          border: '1px solid #1C2526',
          borderRadius: '0',
          maxWidth: '300px'
        },
        onClose: () => navigate('/dashboard')
      }
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        minHeight: '768px',
        maxWidth: '1360px',
        width: '100%',
        bgcolor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 2,
        mx: 'auto',
        overflow: 'auto'
      }}>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          toastStyle={{
            background: '#0A0A0A',
            color: '#FFFFFF',
            border: '1px solid #1C2526',
            borderRadius: '0'
          }}
        />

        <Box sx={{ 
          width: '100%',
          maxWidth: '1200px',
          border: '2px solid #1C2526',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '700px'
        }}>
          {/* Header with ASCII logo */}
          <Box sx={{ 
            p: 3,
            borderBottom: '2px solid #1C2526',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#0A0A0A'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Public sx={{ fontSize: 32, color: '#FFFFFF' }} />
              <Typography component="pre" sx={{ 
                fontFamily: '"IBM Plex Mono", monospace',
                color: '#FFB5C0',
                fontSize: '0.8rem',
                lineHeight: 1.2,
                whiteSpace: 'pre'
              }}>
                {asciiLogo}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ 
              fontFamily: '"IBM Plex Mono", monospace',
              color: '#FFFFFF',
              letterSpacing: '2px',
              fontWeight: 600
            }}>
              {step.toUpperCase()}
            </Typography>
          </Box>

          {/* Stepper */}
          <Stepper 
            activeStep={steps.indexOf(step)} 
            alternativeLabel
            sx={{ p: 3, borderBottom: '2px solid #1C2526', bgcolor: '#0A0A0A' }}
          >
            {stepConfig.map((stepItem, index) => (
              <Step key={stepItem.name}>
                <StepLabel 
                  StepIconComponent={(props) => <DotStepIcon {...props} stepIndex={index} />}
                  sx={{ 
                    '& .MuiStepLabel-label': { 
                      display: 'none' 
                    }
                  }} 
                />
              </Step>
            ))}
          </Stepper>

          {/* Main content */}
          <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
            <AnimatePresence mode="wait">
              {step === 'welcome' && (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    WALLET INITIALIZATION
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#FFFFFF' }}>
                    [SECURE NON-CUSTODIAL CRYPTO VAULT]
                  </Typography>
                  
                  <Card sx={{ p: 3, mb: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          sx={{
                            color: '#1C2526',
                            '&.Mui-checked': {
                              color: '#FFFFFF'
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I UNDERSTAND I AM THE SOLE CUSTODIAN OF MY KEYS
                        </Typography>
                      }
                    />
                    <Button 
                      variant="outlined" 
                      onClick={() => setShowTermsDialog(true)}
                      sx={{ mt: 2 }}
                      startIcon={<VerifiedUser />}
                    >
                      VIEW TERMS
                    </Button>
                  </Card>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}

              {step === 'mode-select' && (
                <motion.div
                  key="mode-select"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    INITIALIZATION MODE
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {[
                      { value: 'new', icon: <AddCircle sx={{ fontSize: 48, color: '#FFFFFF' }} />, label: 'NEW VAULT' },
                      { value: 'recover', icon: <KeyOutlined sx={{ fontSize: 48, color: '#FFFFFF' }} />, label: 'RECOVER VAULT' },
                      { value: 'import', icon: <UploadFile sx={{ fontSize: 48, color: '#FFFFFF' }} />, label: 'IMPORT JSON' }
                    ].map((option) => (
                      <Grid item xs={12} sm={4} key={option.value}>
                        <Card
                          sx={{ 
                            p: 3,
                            cursor: 'pointer',
                            textAlign: 'center',
                            height: '100%',
                            borderColor: mode === option.value ? '#FFFFFF' : '#1C2526',
                            transition: 'all 0.3s cubic-bezier(0.77, 0, 0.175, 1)',
                            '&:hover': { borderColor: '#FFFFFF', boxShadow: '0 0 15px rgba(0,255,0,0.3)' }
                          }}
                          onClick={() => setMode(option.value)}
                        >
                          <Box sx={{ mb: 2 }}>
                            {option.icon}
                          </Box>
                          <Typography variant="h6">
                            {option.label}
                          </Typography>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}

              {step === 'seed-gen' && (
                <motion.div
                  key="seed-gen"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    {mode === 'new' ? 'SECURE RECOVERY PHRASE' : mode === 'recover' ? 'ENTER RECOVERY PHRASE' : 'IMPORT JSON VAULT'}
                  </Typography>
                  
                  {mode === 'new' && mnemonic && (
                    <Box sx={{ mb: 3 }}>
                      <Card sx={{ p: 3, mb: 2 }}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {mnemonic.split(' ').map((word, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <Chip
                                label={showMnemonic ? word : '••••'}
                                onClick={() => showMnemonic && handleCopyToClipboard(word, true)}
                                sx={{ width: '100%', cursor: showMnemonic ? 'pointer' : 'default' }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            startIcon={<ContentCopy />}
                            onClick={() => handleCopyToClipboard(mnemonic)}
                          >
                            COPY PHRASE
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={showMnemonic ? <VisibilityOff /> : <Visibility />}
                            onClick={() => setShowMnemonic(!showMnemonic)}
                          >
                            {showMnemonic ? 'HIDE' : 'SHOW'}
                          </Button>
                        </Box>
                      </Card>
                      <Alert severity="warning">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Warning sx={{ fontSize: 20 }} />
                          <Typography>NEVER SHARE YOUR RECOVERY PHRASE</Typography>
                        </Box>
                      </Alert>
                    </Box>
                  )}

                  {mode === 'recover' && (
                    <Box sx={{ mb: 3 }}>
                      <Card sx={{ p: 3, mb: 2 }} onPaste={handlePasteMnemonic}>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          {recoveryWords.map((word, index) => (
                            <Grid item xs={6} sm={4} md={3} key={index}>
                              <TextField
                                value={word}
                                onChange={(e) => handleRecoveryInputChange(index, e.target.value)}
                                placeholder={`WORD ${index + 1}`}
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                  endAdornment: word && (
                                    <InputAdornment position="end">
                                      <IconButton onClick={() => handleCopyToClipboard(word, true)} sx={{ p: 0 }} size="small">
                                        <ContentCopy />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Card>
                      <Alert severity="info">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Info sx={{ fontSize: 20 }} />
                          <Typography>PASTE OR ENTER 12 WORDS IN ORDER</Typography>
                        </Box>
                      </Alert>
                    </Box>
                  )}

                  {mode === 'import' && (
                    <Box sx={{ mb: 3 }}>
                      <Card sx={{ p: 3, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<UploadFile />}
                          sx={{ mb: 2 }}
                        >
                          UPLOAD JSON
                          <input type="file" accept=".json" hidden onChange={handleFileUpload} />
                        </Button>
                        {importJson && (
                          <Alert severity="success">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CheckCircle sx={{ fontSize: 20 }} />
                              <Typography>JSON FILE UPLOADED</Typography>
                            </Box>
                          </Alert>
                        )}
                      </Card>
                    </Box>
                  )}
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}

              {step === 'verify' && (
                <motion.div
                  key="verify"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    VERIFY RECOVERY PHRASE
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Info sx={{ fontSize: 20 }} />
                      <Typography>ENTER THE REQUESTED WORDS FROM YOUR RECOVERY PHRASE TO VERIFY YOU SAVED IT CORRECTLY</Typography>
                    </Box>
                  </Alert>
                  
                  <Card sx={{ p: 3, mb: 3 }}>
                    {verifyWords.map((item, index) => {
                      const isIncorrect = userVerifyInputs[index] && 
                                          userVerifyInputs[index].toLowerCase() !== item.word.toLowerCase();
                      return (
                        <TextField
                          key={index}
                          label={`WORD #${item.index}`}
                          value={userVerifyInputs[index]}
                          onChange={(e) => {
                            const newInputs = [...userVerifyInputs];
                            newInputs[index] = e.target.value;
                            setUserVerifyInputs(newInputs);
                            setErrorMessage(''); // Clear error when typing
                          }}
                          error={isIncorrect}
                          helperText={isIncorrect ? `Incorrect - Please verify word #${item.index}` : ''}
                          variant="outlined"
                          fullWidth
                          sx={{ 
                            mb: 2,
                            '& .MuiOutlinedInput-root.Mui-error': {
                              '& fieldset': { borderColor: '#FF4D4D' }
                            },
                            '& .MuiFormHelperText-root.Mui-error': {
                              color: '#FF4D4D'
                            }
                          }}
                        />
                      );
                    })}
                  </Card>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}

              {step === 'security-info' && (
                <motion.div
                  key="security-info"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    🔐 SECURE YOUR WALLET
                  </Typography>

                  <Card sx={{ p: 4, mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, textAlign: 'center', color: '#FFFFFF' }}>
                      ENCRYPTION & AUTHENTICATION SETUP
                    </Typography>

                    <Box sx={{ mb: 3, p: 3, border: '1px solid #1C2526', backgroundColor: '#0A0A0A' }}>
                      <Typography variant="body1" sx={{ mb: 2, lineHeight: 2 }}>
                        You are about to encrypt your wallet with a password and two-factor authentication (2FA).
                      </Typography>

                      <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#FFFFFF' }}>
                        ⚠️ IMPORTANT:
                      </Typography>

                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
                        <li>
                          <Typography variant="body2">
                            <strong>PASSWORD:</strong> Encrypts your wallet locally on this device
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            <strong>2FA:</strong> Adds an extra layer of security for all transactions
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            <strong>STORAGE:</strong> Store your password and 2FA backup codes in a safe place
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            <strong>ACCESS:</strong> You will need BOTH to access your wallet and sign transactions
                          </Typography>
                        </li>
                        {(mode === 'new' || mode === 'recover') && (
                          <>
                            <li>
                              <Typography variant="body2">
                                <strong>RECOVERY:</strong> If you lose your password or 2FA, you can recover using your 12-word phrase
                              </Typography>
                            </li>
                            <li>
                              <Typography variant="body2" sx={{ color: '#FF4D4D' }}>
                                <strong>⚠️ WARNING:</strong> Without your recovery phrase, lost credentials mean PERMANENT loss of access
                              </Typography>
                            </li>
                          </>
                        )}
                        {mode === 'import' && (
                          <li>
                            <Typography variant="body2">
                              <strong>BACKUP:</strong> Your imported wallet uses the keystore file as backup
                            </Typography>
                          </li>
                        )}
                      </Box>

                      <Typography variant="h6" sx={{ mt: 4, mb: 2, color: '#FFFFFF' }}>
                        📋 NEXT STEPS:
                      </Typography>

                      <Box component="ol" sx={{ pl: 2, '& li': { mb: 1.5, lineHeight: 1.8 } }}>
                        <li>
                          <Typography variant="body2">
                            Create a strong password (8+ characters with uppercase, lowercase, numbers, and symbols)
                          </Typography>
                        </li>
                        <li>
                          <Typography variant="body2">
                            Configure two-factor authentication (scan QR code with your authenticator app)
                          </Typography>
                        </li>
                      </Box>
                    </Box>

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Shield sx={{ fontSize: 20 }} />
                        <Typography>
                          These security measures protect your funds. Take your time to set them up correctly.
                        </Typography>
                      </Box>
                    </Alert>
                  </Card>
                </motion.div>
              )}

              {step === 'password' && (
                <motion.div
                  key="password"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    SECURE YOUR VAULT
                  </Typography>
                  
                  <Card sx={{ p: 3, mb: 3 }}>
                    <TextField
                      label="PASSWORD"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      error={password && !validatePassword(password)}
                      helperText={password && !validatePassword(password) ? 'Use 8+ characters, uppercase, lowercase, numbers, symbols' : ''}
                      variant="outlined"
                      fullWidth
                      sx={{ mb: 2 }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ p: 0 }} size="small">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    
                    <TextField
                      label="CONFIRM PASSWORD"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      error={confirmPassword && password !== confirmPassword}
                      helperText={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : ''}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} sx={{ p: 0 }} size="small">
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Card>
                  <Alert severity="info">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Info sx={{ fontSize: 20 }} />
                      <Typography>USE UPPERCASE, LOWERCASE, NUMBERS AND SYMBOLS</Typography>
                    </Box>
                  </Alert>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}

              {step === '2fa' && (
                <motion.div
                  key="2fa"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
                    TWO-FACTOR AUTHENTICATION
                  </Typography>
                  
                  <Card sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <QRCode
                        value={`otpauth://totp/OverEndurance:${walletAddress}?secret=${twoFASecret}&issuer=OverEndurance`}
                        size={180}
                        level="H"
                        bgColor="#000000"
                        fgColor="#FFFFFF"
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                      <Google sx={{ color: '#FFFFFF' }} />
                      <Phone sx={{ color: '#FFFFFF' }} />
                      <Lock sx={{ color: '#FFFFFF' }} />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', mb: 3 }}>
                      <Typography sx={{ 
                        fontFamily: '"IBM Plex Mono", monospace',
                        bgcolor: '#0A0A0A',
                        p: 1.5,
                        border: '1px solid #1C2526',
                        letterSpacing: '2px',
                        color: '#FFFFFF'
                      }}>
                        {twoFASecret.match(/.{1,4}/g)?.join(' ') || ''}
                      </Typography>
                      <IconButton onClick={() => handleCopyToClipboard(twoFASecret)} sx={{ p: 0 }} size="small">
                        <ContentCopy sx={{ color: '#FFFFFF' }} />
                      </IconButton>
                    </Box>
                    
                    {(mode === 'import' || mode === 'recover') && (
                      <Button
                        variant="outlined"
                        startIcon={<RestartAlt />}
                        onClick={handleReset2FA}
                        sx={{ mb: 3 }}
                      >
                        RESET 2FA
                      </Button>
                    )}
                    
                    <TextField
                      label="2FA CODE"
                      value={twoFACode}
                      onChange={(e) => /^\d*$/.test(e.target.value) && setTwoFACode(e.target.value)}
                      inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                      variant="outlined"
                      fullWidth
                      error={twoFACode && !validateTwoFACode(twoFACode)}
                      helperText={twoFACode && !validateTwoFACode(twoFACode) ? 'Invalid 2FA code' : ''}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><QrCode sx={{ color: '#FFFFFF' }} /></InputAdornment>
                      }}
                    />
                  </Card>
                  {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ErrorOutline sx={{ fontSize: 20 }} />
                        <Typography>{errorMessage}</Typography>
                      </Box>
                    </Alert>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {/* Actions */}
          <Box sx={{ 
            p: 3,
            borderTop: '2px solid #1C2526',
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: '#0A0A0A'
          }}>
            {step !== 'welcome' && (
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => setStep(steps[steps.indexOf(step) - 1])}
                disabled={isProcessing}
              >
                BACK
              </Button>
            )}
            
            <Button
              variant="outlined"
              endIcon={isProcessing ? <CircularProgress size={24} sx={{ color: '#FFFFFF' }} /> : <ArrowForward />}
              onClick={handleContinue}
              disabled={isProcessing || (step === 'welcome' && !acceptedTerms)}
            >
              {isProcessing ? 'PROCESSING' : step === '2fa' ? 'FINALIZE' : 'NEXT'}
            </Button>
          </Box>
        </Box>

        {/* Terms dialog */}
        <Dialog
          open={showTermsDialog}
          onClose={() => setShowTermsDialog(false)}
          PaperProps={{ sx: { 
            background: '#0A0A0A',
            border: '2px solid #1C2526',
            borderRadius: '0',
            maxWidth: '800px',
            width: '100%'
          }}}
        >
          <DialogTitle sx={{ 
            fontSize: '1.6rem',
            borderBottom: '2px solid #1C2526',
            display: 'flex',
            alignItems: 'center',
            color: '#FFFFFF'
          }}>
            <VerifiedUser sx={{ mr: 2, color: '#FFFFFF' }} /> TERMS AND PRIVACY
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body1" sx={{ mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              TERMS OF SERVICE
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.8 }}>
              AVO STATION IS A NON-CUSTODIAL CRYPTOCURRENCY WALLET FOR THE AVO NETWORK. BY USING THIS SERVICE, YOU ACKNOWLEDGE AND AGREE TO THE FOLLOWING:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • YOU ARE SOLELY RESPONSIBLE FOR YOUR PRIVATE KEYS, RECOVERY PHRASE, AND ALL WALLET SECURITY
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • WE DO NOT HAVE ACCESS TO YOUR PRIVATE KEYS OR FUNDS AND CANNOT RECOVER LOST CREDENTIALS
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • WE ARE NOT LIABLE FOR ANY LOST, STOLEN, OR MISUSED FUNDS
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • ALL TRANSACTIONS ON THE AVO NETWORK ARE IRREVERSIBLE
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.8 }}>
              • THESE TERMS MAY BE UPDATED WITHOUT PRIOR NOTICE
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              PRIVACY POLICY
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • WE DO NOT COLLECT OR STORE PERSONAL INFORMATION
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • YOUR WALLET DATA IS ENCRYPTED LOCALLY ON YOUR DEVICE
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • BLOCKCHAIN TRANSACTIONS ARE PUBLIC BY DESIGN AND VISIBLE ON THE AVO NETWORK
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.8 }}>
              • WE MAY USE ANONYMOUS ANALYTICS TO IMPROVE USER EXPERIENCE
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
              • NO THIRD PARTIES HAVE ACCESS TO YOUR WALLET OR PRIVATE KEYS
            </Typography>
          </DialogContent>
          <DialogActions sx={{ borderTop: '2px solid #1C2526', p: 2 }}>
            <Button onClick={() => setShowTermsDialog(false)} startIcon={<ArrowBack />}>
              CLOSE
            </Button>
            <Button 
              onClick={() => { setAcceptedTerms(true); setShowTermsDialog(false); }} 
              variant="outlined"
              startIcon={<Check />}
            >
              ACCEPT
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default CreateWallet;
